import { addEvent, triggerEvent, removeEvent, Throttle } from '../utils/events';
import {
  getElementWidth,
  clearStyles,
  hasClass,
  addClass,
  addClasses,
  removeClass,
  removeElement,
  wrap,
  unwrap,
} from '../utils/html';

export class ButtonGroup {
  constructor(buttonGroup) {
    this.buttonGroup = buttonGroup;
    this.magicNumberForResizeThrottling = 10;
    if (!this.isEverythingOk()) {
      return;
    }
    this.init();
  }

  init() {
    if (this.buttonGroup) {
      const buttonGroupItems = this.buttonGroup.children;
      for (const buttonGroupItem of buttonGroupItems) {
        const input = buttonGroupItem.getElementsByClassName('btn-group-item__input')[0];
        addEvent(input, 'click', this.apply.bind(this));

        const labels = buttonGroupItem.getElementsByClassName('btn-group-item__label');
        for (const label of labels) {
          if (label.getAttribute('draggable')) {
            addEvent(label, 'dragstart', this.onDrag.bind(this));
            addEvent(label, 'dragenter', this.onDragEnter.bind(this));
            addEvent(label, 'dragover', this.onDragOver.bind(this));
            addEvent(label, 'dragleave', this.onDragLeave.bind(this));
            addEvent(label, 'drop', this.onDragDrop.bind(this));
          }
        }
      }

      // Snapshot the initial dimensions of the button group so that we can refer to them later
      this.responsiveMode = false;
      this.initialWidth = getElementWidth(this.buttonGroup);
      this.checkForResponsive();

      this.windowResizeListener = this.onWindowResize.bind(this);
      this.windowResize = new Throttle(this.windowResizeListener, this.magicNumberForResizeThrottling);
      addEvent(window, 'resize', this.windowResize.trigger);

      this.disabledToTop();
    }
  }

  onWindowResize() {
    this.checkForResponsive();
  }

  checkForResponsive() {
    // Check if we need to convert to dropdown
    const currentWindowWidth = window.innerWidth;
    const currentWidth = getElementWidth(this.buttonGroup);
    const containerWidth = getElementWidth(this.buttonGroup.parentNode);
    if (!this.responsiveMode &&
      containerWidth < currentWidth) {
      this.enableResponsiveMode();
      this.responsiveModeWindowWidth = currentWindowWidth;
    } else if (this.responsiveMode &&
      currentWindowWidth > this.responsiveModeWindowWidth) {
      this.disableResponsiveMode();
    }
  }

  apply() {
    triggerEvent(this.buttonGroup, 'apply.commonui.buttonGroup');
    this.checkForResponsive();
  }

  enableResponsiveMode() {
    this.responsiveMode = true;
    wrap(this.buttonGroup, 'dropdown');

    this.dropdown = this.buttonGroup.parentNode;

    addClass(this.buttonGroup, 'dropdown-menu');

    // First check if an option is already selected, as we need to grab the text from that option
    const selectedOption = this.buttonGroup.querySelector('input[type="radio"]:checked');
    const buttonText = selectedOption ? selectedOption.parentNode.querySelector('label').innerText : 'Please select';
    const buttonHtml = `<span class="js-btn-text push--right">${ buttonText }</span> <i class="fa fa-caret"/>`;
    // Create the dropdown button
    this.dropdownButton = document.createElement('button');
    this.dropdownButton.innerHTML = buttonHtml;

    addClasses(this.dropdownButton, [ 'btn', 'btn-dropdown', 'js-dropdown-btn' ]);

    // Check if we need to go full width
    if (hasClass(this.buttonGroup, 'btn-group--full-width')) {
      addClass(this.dropdown, 'width--100');
    } else {
      // Get the width of the button group
      addClass(this.dropdown, 'dropdown-open');
      const buttonGroupWidth = getElementWidth(this.buttonGroup);
      removeClass(this.dropdown, 'dropdown-open');
      const buttonPadding = 40;
      const buttonWidth = `${ buttonGroupWidth + buttonPadding }px`;
      this.dropdownButton.style.width = buttonWidth;
      this.buttonGroup.style.width = buttonWidth;
    }

    // Inject the dropdown button before the button group
    this.buttonGroup.before(this.dropdownButton);

    this.openDropdownListener = this.openDropdown.bind(this);
    this.closeDropdownListener = this.closeDropdown.bind(this);
    this.documentClickListener = this.onDocumentClick.bind(this);
    this.selectOptionListener = this.selectOption.bind(this);

    addEvent(this.dropdownButton, 'click', this.openDropdownListener);
  }

  disableResponsiveMode() {
    this.responsiveMode = false;

    // Unbind the "click" event on the dropdown button
    removeEvent(this.dropdownButton, 'click', this.openDropdownListener);

    // Remove the "dropdown-menu" class from the button group container
    removeClass(this.buttonGroup, 'dropdown-menu');

    // Remove all inline styles from the button group as the width will have been set
    clearStyles(this.buttonGroup);

    // Unwrap the button group to remove the "dropdown" container
    unwrap(this.buttonGroup);

    // Remove the dropdown button
    removeElement(this.dropdownButton);
  }

  openDropdown(event) {
    event.preventDefault();
    event.stopPropagation();

    addClass(this.dropdown, 'dropdown-open');

    removeEvent(this.dropdownButton, 'click', this.openDropdownListener);

    addEvent(this.dropdownButton, 'click', this.closeDropdownListener);
    addEvent(document, 'click', this.documentClickListener);

    for (const buttonGroupOption of this.buttonGroup.querySelectorAll('.btn-group-item__label')) {
      addEvent(buttonGroupOption, 'click', this.selectOptionListener);
    }
  }

  closeDropdown() {
    this.dropdown.classList.remove('dropdown-open');
    removeEvent(document, 'click', this.documentClickListener);
    for (const buttonGroupOption of this.buttonGroup.querySelectorAll('.btn-group-item__label')) {
      removeEvent(buttonGroupOption, 'click', this.selectOptionListener);
    }
    removeEvent(this.dropdownButton, 'click', this.closeDropdownListener);
    addEvent(this.dropdownButton, 'click', this.openDropdownListener);
  }

  onDocumentClick(event) {
    if (!this.dropdown.contains(event.target)) {
      this.closeDropdown();
    }
  }

  selectOption(event) {
    const label = event.target;
    const btnText = this.dropdownButton.querySelector('.js-btn-text');
    btnText.innerText = label.innerText;
    this.closeDropdown();
  }

  onDrag(event) {
    this.parent = event.target.parentNode.parentNode;
    this.child = event.target.parentNode;

    event.dataTransfer.setData('Text', this.child);

    event.dataTransfer.effectAllowed = 'move';
  }

  onDragEnter(event) {
    event.preventDefault();
  }

  onDragOver(event) {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    event.target.classList.add('button--dragover');
  }

  onDragLeave(event) {
    event.preventDefault();
    event.target.classList.remove('button--dragover');
  }

  onDragDrop(event) {
    event.preventDefault();
    event.target.classList.remove('button--dragover');
    triggerEvent(event.target.parentNode.parentNode, 'drop.commonui.buttonGroup');

    if (this.child.getAttribute('data-id') > event.target.parentNode.getAttribute('data-id')) {
      this.parent.insertBefore(this.child, event.target.parentNode);
    } else {
      this.parent.insertBefore(this.child, event.target.parentNode.nextSibling);
    }

    let index = 0;
    for (const child of this.parent.children) {
      child.setAttribute('data-id', index.toString());
      index += 1;
    }

    event.stopImmediatePropagation();
  }

  disabledToTop() {
    const buttonGroup = [ ...this.buttonGroup.children ];
    for (const listItem of buttonGroup.reverse()) {
      if (listItem.hasAttribute('disabled')) {
        this.buttonGroup.insertBefore(listItem, this.buttonGroup.children[0]);
      }
    }
  }

  destroy() {
    this.close();
  }

  /* eslint-disable no-console */
  isEverythingOk() {
    if (!this.buttonGroup) {
      console.warn('CommonUI - Button - There\'s no button group found');
      return false;
    }
    return true;
  }
  /* eslint-enable no-console */
}

/**
 * Creates a new button instance
 * @param  {Object} config
 */
export default function initButtonGroup(config = '.js-btn-group') {
  const selector = config;
  let buttonGroup = null;
  if (typeof selector === 'string' && selector === '.js-btn-group') {
    for (const elem of document.querySelectorAll(selector)) {
      buttonGroup = new ButtonGroup(elem);
    }
  } else {
    buttonGroup = new ButtonGroup(selector);
  }
  return buttonGroup;
}



// WEBPACK FOOTER //
// ./js/components/ButtonGroup.js